<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'SalesStats',
  data() {
    return {
      subTitles: [
        'online',
        'phone',
        'total',
      ],
      headers: [],
      uniqueProductTypes: [],
      paginationSort: {
        sortBy: 'stats.total.total',
        descending: true,
        rowsPerPage: -1,
      },
      bookedDateFrom: this.formatDateToISOString(this.getFirstDayInLastMonth()),
      bookedDateTo: this.formatDateToISOString(this.getLastDayInLastMonth()),
      bookedDateFromMenu: false,
      bookedDateToMenu: false,
    };
  },
  mounted() {
    this.loadBrands();
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: this.$t('menu.stats.title'), to: '', disabled: true },
        { text: this.$t('menu.stats.pages.stats.title'), to: this.$route.path },
      ],
      title: this.$t('menu.stats.pages.stats.title'),
    });
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      loadBrands: 'twenty/brandsLoad',
      setBrands: 'twenty/brandsSet',
      loadSalesStats: 'twenty/salesStatsLoad',
    }),
    changeBrands(brands) {
      this.setBrands(brands);
    },
    changeDates() {
      this.bookedDateFromMenu = false;
      this.bookedDateToMenu = false;
    },
    refreshSalesStats() {
      if (this.selectedBrands && this.selectedBrands.length && !this.salesStatsLoading) {
        this.loadSalesStats({
          brandIds: this.selectedBrands,
          from: this.bookedDateFrom,
          to: this.bookedDateTo,
        });
      }
    },
    refreshBrands() {
      if (this.brandsError) {
        this.loadBrands();
      }
    },
    generateUniqueProductTypes() {
      const salesStats = this.salesStats;
      const uniqueProductTypes = [];
      if (salesStats) {
        salesStats.forEach((employee) => {
          if (employee.productTypes) {
            employee.productTypes.forEach((productType) => {
              uniqueProductTypes[productType.name] = true;
            });
          }
        });
      }
      this.uniqueProductTypes = Object.keys(uniqueProductTypes);
    },
    generateHeaders() {
      const headers = [
        { text: '' },
        { text: 'Total' },
      ];
      this.uniqueProductTypes.forEach((productType) => {
        headers.push({ text: productType });
      });
      this.headers = headers;
    },
    getFirstDayInLastMonth() {
      const date = new Date();
      return new Date(date.getFullYear(), date.getMonth() - 1, 2);
    },
    getLastDayInLastMonth() {
      const date = new Date();
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    formatDateToISOString(date) {
      return new Date(date).toISOString().substr(0, 10);
    },
    formatDate(date) {
      return date
        ? this.$moment(date).format('DD-MM-YYYY')
        : '';
    },
  },
  computed: {
    ...mapState({
      brandsError: state => state.twenty.brands.itemsError,
      brandsLoading: state => state.twenty.brands.itemsLoading,
      brands: state => state.twenty.brands.items,
      selectedBrands: state => state.twenty.brands.selectedBrands,
      salesStatsError: state => state.twenty.salesStats.itemsError,
      salesStatsLoading: state => state.twenty.salesStats.itemsLoading,
      salesStats: state => state.twenty.salesStats.items,
    }),
    canSearch() {
      return this.selectedBrands && this.selectedBrands.length;
    },
    salesStatsGenerated() {
      const salesStats = this.salesStats;
      if (!salesStats) {
        return [];
      }
      this.generateUniqueProductTypes();
      this.generateHeaders();
      const employees = [];
      salesStats.forEach((employee) => {
        const employeeStats = {};
        employeeStats.total = {
          online: 0,
          phone: 0,
          total: 0,
        };
        this.uniqueProductTypes.forEach((uniqueProductType) => {
          employeeStats[uniqueProductType] = {
            online: 0,
            phone: 0,
            total: 0,
          };
        });

        if (employee.productTypes) {
          let totalOnline = 0;
          let totalPhone = 0;
          employee.productTypes.forEach((productType) => {
            const onlineBookings = productType.onlineBookings;
            const manualBookings = productType.manualBookings;
            totalOnline += onlineBookings;
            totalPhone += manualBookings;
            employeeStats[productType.name] = {
              online: onlineBookings,
              phone: manualBookings,
              total: onlineBookings + manualBookings,
            };
          });
          employeeStats.total = {
            online: totalOnline,
            phone: totalPhone,
            total: totalOnline + totalPhone,
          };
        }
        employees.push({
          name: employee.name,
          stats: employeeStats,
        });
      });

      return employees;
    },
    loading() {
      return this.brandsLoading || this.salesStatsLoading;
    },
  },
};
</script>
<template>
  <v-flex xs12>
    <v-layout row wrap class="padding-15">
      <v-flex xs4 pa-2>
        <v-select
          @input="changeBrands"
          @click:append="refreshBrands"
          :value="selectedBrands"
          :items="brands"
          :loading="brandsLoading"
          :label="$t('salesStats.brands.choose')"
          :disabled="brandsLoading || salesStatsLoading"
          :error-messages="brandsError ? $t('salesStats.brands.error') : undefined"
          :error="!!brandsError"
          :append-icon="!brandsError ? 'filter_list' : 'refresh'"
          item-value="id"
          item-text="name"
          dense
          multiple
        >
          <template v-slot:selection="{ item, index }">
            <v-chip color="green" text-color="white">
              <span>{{ item.name }}</span>
            </v-chip>
          </template>
        </v-select>
      </v-flex>
      <v-flex xs2 pa-3>
        <v-menu
          v-model="bookedDateFromMenu"
          :close-on-content-click="false"
          full-width
          max-width="290"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-on="on"
              :value="formatDate(bookedDateFrom)"
              :disabled="salesStatsLoading"
              label="Booked from"
              readonly
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="bookedDateFrom"
            @change="changeDates"
          ></v-date-picker>
        </v-menu>
      </v-flex>
      <v-flex xs2 pa-3>
        <v-menu
          v-model="bookedDateToMenu"
          :close-on-content-click="false"
          full-width
          max-width="290"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-on="on"
              :value="formatDate(bookedDateTo)"
              :disabled="salesStatsLoading"
              label="Booked to"
              readonly
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="bookedDateTo"
            @change="changeDates"
          ></v-date-picker>
        </v-menu>
      </v-flex>
      <v-flex xs1 pa-3>
        <v-btn
          @click="refreshSalesStats"
          :disabled="!this.canSearch || this.salesStatsLoading"
          color="success"
          large>
          Search
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex xs12>
        <v-container pa-0>
          <v-data-table
            :headers="headers"
            :items="salesStatsGenerated"
            :loading="salesStatsLoading"
            :pagination.sync="paginationSort"
            :loading-text="$t('salesStats.load.loading')"
            must-sort
            hide-actions
          >
            <template v-if="salesStatsGenerated.length" slot="headers" slot-scope="props">
                <th
                  v-for="header in props.headers"
                  :key="header.text"
                  class="column text-xs-left"
                >
                  <span class="padding-left-14 font-size-10">{{ header.text }}</span>
                  <table v-if="header.text.length">
                    <tr class="text-xs-right">
                      <td
                        v-for="subTitle in subTitles"
                        v-if="
                          header.text !== 'Total'
                          || (header.text === 'Total' && subTitle !== 'total')
                        "
                        :key="subTitle.text"
                      >
                        {{ subTitle }}
                      </td>
                    </tr>
                  </table>
                </th>
            </template>
            <template slot="items" slot-scope="props">
              <tr>
                <td>{{ props.item.name }}</td>
                <td
                  v-for="(stats, statsType) in props.item.stats"
                  :key="statsType"
                  class="border-left-solid-grey"
                >
                  <table>
                    <tr class="text-xs-right">
                      <td :class="{'font-weight-bold': statsType === 'total'}">
                        {{ stats.online }}
                      </td>
                      <td :class="{'font-weight-bold': statsType === 'total'}">
                        {{ stats.phone }}
                      </td>
                      <td v-if="statsType !== 'total'" class="font-weight-bold">
                        {{ stats.total }}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </template>
            <template slot="no-data">
              <div v-if="!selectedBrands" class="text-xs-center">
                <span>{{ $t('salesStats.brands.choose-info') }}</span>
              </div>
              <div v-else-if="salesStatsError">
                <v-alert :value="true" icon="warning">
                  {{ `${$t('salesStats.load.error')} (${this.salesStatsError})` }}
                </v-alert>
              </div>
              <div v-else-if="!salesStatsLoading" class="text-xs-center">
                <span>{{ $t('salesStats.noData') }}</span>
              </div>
              <div v-else class="text-xs-center">
                {{ $t('salesStats.load.loading') }}
              </div>
            </template>
          </v-data-table>
        </v-container>
      </v-flex>
    </v-layout>
  </v-flex>
</template>
<style>
  th {
    padding: 0 10px !important;
  }

  td {
    padding: 0 10px !important;
  }

  td::after {
    content: '';
    display: block;
    width: 35px;
  }

  .padding-15 {
    padding: 15px;
  }

  .padding-left-14 {
    padding-left: 14px;
  }

  .font-size-10 {
    font-size: 18px;
  }

  .border-left-solid-grey {
    border-left: 1px solid rgba(128, 128, 128, 0.5);
  }
</style>



// WEBPACK FOOTER //
// src/pages/stats/SalesStats.vue